import { useContext, useState, useEffect, useCallback, useRef } from "react";
import { GlobalContext, FunctionsContext } from "../common/Context";
import { clearCache } from "../IndexedDBApi/IDBCache";
import { useTranslation } from "react-i18next";
import moment from "moment";

const CURRENT_USER_INITIAL_STATE = {
  profileID: null,
  companyID: null,
  authUser: null,
};

const initAuthObj = () => ({
  auth: null,
  profile: null,
  token: null,
  tokenFetched: false,
  settingClaim: false,
  claimSet: false,
  userInfo: null,
  userInfoSet: false,
  authDone: false,
  forceToken: false,
});

const useFirebaseAuthentication = (fb) => {
  const { t } = useTranslation("Account");
  const {
    firebaseState,
    setSignLoad,
    setSignError,
    setSignUpEmail,
    setSignUpInviteId,
    setCustomToken,
    setTokenLoading,
    setCompetitionList,
    setCompanyState,
    userStateEmail,
    userState,
    setUserState,
    setUserTopTryps,
    setRankData,
  } = useContext(GlobalContext);
  const { getCompanyById } = useContext(FunctionsContext);

  const week = useRef(moment().format("YYYY-WW"));

  const [currentUser, setCurrentUser] = useState(CURRENT_USER_INITIAL_STATE);
  const [appFirstLoading, setAppFirstLoading] = useState(true);
  const [authObj, setAuthObj] = useState(initAuthObj());

  const userCompany = getCompanyById(userState?.companyID);

  const resetAuth = useCallback(() => {
    setCurrentUser(CURRENT_USER_INITIAL_STATE);
    setAuthObj(initAuthObj());
    setSignLoad(false);
    setAppFirstLoading(false);
    clearCache();
    sessionStorage.clear();
  }, [setSignLoad]);

  const badSignIn = useCallback(async () => {
    await firebaseState.doSignOut();
    setSignError(t("noAccess"));
    resetAuth();
    setSignLoad(false);
    setAppFirstLoading(false);
    setTokenLoading(false);
  }, [firebaseState, setSignError, resetAuth, t]);

  const getToken = useCallback(async () => {
    try {
      setSignLoad(true);
      const token = await authObj.auth?.getIdTokenResult(authObj.forceToken);
      if (!token.claims?.medidas) {
        await badSignIn();
        return;
      }

      setAuthObj((prev) => ({
        ...prev,
        token,
        tokenFetched: true,
        forceToken: false,
      }));
    } catch (e) {
      console.error(`Auth error: getIdTokenResult(${authObj.forceToken})`);
      console.error(e);
    }
  }, [authObj.auth, authObj.forceToken]);

  const getProfile = useCallback(async () => {
    setAuthObj((prev) => ({ ...prev, userFetching: true }));
    try {
      if (authObj.auth) {
        const { profile, teams } = await firebaseState.getProfileAndTeamsById();
        profile.teams = teams.map((group) => group.group ?? group);
        setAuthObj((prev) => ({
          ...prev,
          profile: profile,
          userFetched: true,
          userFetching: false,
        }));
      }
    } catch (e) {
      console.error(e);
    }
  }, [authObj.auth, authObj.token, firebaseState, setSignLoad]);

  const getUserCompetitions = useCallback(async () => {
    try {
      if (authObj.auth?.uid) {
        const competitions = await firebaseState.getActiveCompsByUser(
          authObj.token?.claims.medidas ?? false,
        );
        if (competitions) {
          setCompetitionList(competitions);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [firebaseState, authObj.auth, authObj.token, setCompetitionList]);

  const updateUserState = useCallback(async () => {
    try {
      const [profile, fbProfile] = await Promise.all([
        firebaseState.getProfileByEmail(userStateEmail),
        firebaseState.adminGetUserByEmail(userStateEmail),
      ]);
      const userTeams = await firebaseState.listAllUserTeams(profile?.id);
      setUserState({
        profileID: profile?.id,
        level: profile?.level,
        tlId: profile?.idUserTL,
        avatar: profile?.avatar,
        email: profile?.email,
        fn: profile?.name,
        ln: profile?.lastName,
        isMedidas: fbProfile.customClaims.medidas,
        isAdmin: fbProfile.customClaims.tlca,
        isViewer: fbProfile.customClaims.tlc,
        isPremium: fbProfile.customClaims.tlcp,
        companyID: profile?.uidCompany,
        teams: userTeams.map((group) => group.group ?? group),
        authUser: authObj.auth,
      });
    } catch (e) {
      console.error(e);
    }
  }, [authObj.auth, firebaseState, setUserState, userStateEmail]);

  const finishAuth = useCallback(() => {
    setSignUpEmail(null);
    setSignUpInviteId(null);
    setCustomToken(null);
    setSignError("");
    setTokenLoading(false);
    setSignLoad(false);
    setAppFirstLoading(false);
    setCurrentUser((prev) => ({
      ...prev,
      authUser: authObj.auth,
      ...authObj.userInfo,
    }));
    setAuthObj((prev) => ({ ...prev, authDone: true }));
  }, [
    authObj.userInfo,
    authObj.auth,
    setSignUpEmail,
    setSignUpInviteId,
    setCustomToken,
    setSignError,
    setTokenLoading,
  ]);

  /* First step, get token (or refresh token if custom claims set) */
  useEffect(() => {
    authObj.auth && !authObj.tokenFetched && getToken();
  }, [authObj.auth, authObj.tokenFetched, getToken]);

  /* Get profile */
  useEffect(() => {
    if (
      authObj.tokenFetched &&
      !authObj.authDone &&
      !authObj.userFetching &&
      !authObj.userFetched
    ) {
      getProfile();
    }
  }, [
    authObj.auth,
    authObj.tokenFetched,
    authObj.token,
    authObj.authDone,
    authObj.userFetching,
    authObj.userFetched,
    getProfile,
  ]);

  /* Set user info after getting profile */
  useEffect(() => {
    if (authObj.userFetched && authObj.profile) {
      setAuthObj((prev) => ({
        ...prev,
        userInfo: {
          profileID: authObj.profile?.id,
          level: authObj.profile?.level,
          tlId: authObj.profile?.idUserTL,
          avatar: authObj.profile?.avatar,
          email: authObj.profile?.email,
          fn: authObj.profile?.name,
          ln: authObj.profile?.lastName,
          isMedidas: authObj.token?.claims?.medidas,
          isAdmin: authObj.token?.claims?.tlca,
          isViewer: authObj.token?.claims?.tlc,
          isPremium: authObj.token?.claims?.tlcp,
          companyID: authObj.profile?.uidCompany,
          teams: authObj.profile?.teams,
        },
        userInfoSet: true,
      }));
    }
  }, [authObj.profile, authObj.userFetched]);

  /* Last Step */
  useEffect(() => {
    if (!authObj.authDone && authObj.userInfoSet) {
      finishAuth();
    }
  }, [
    authObj.authDone,
    authObj.userInfo,
    authObj.userInfoSet,
    badSignIn,
    finishAuth,
  ]);

  /* Setup */
  useEffect(() => {
    const fetchData = async (auth) => {
      setAuthObj({ ...initAuthObj(), auth });
    };
    const unlisten = fb.auth().onAuthStateChanged((auth) => {
      return auth ? fetchData(auth) : resetAuth();
    });
    return () => {
      unlisten();
    };
  }, [fb, resetAuth, setTokenLoading]);

  /* Other hooks */
  /* Company state */
  useEffect(() => {
    if (userCompany.error) {
      console.error(userCompany.error);
      return;
    }
    if (userCompany.isLoading) {
      return;
    }
    if (userCompany.data) {
      setCompanyState({
        companyID: userCompany.data.id,
        iconURL: userCompany.data.iconUrl,
        companyName: userCompany.data.companyName,
      });
    }
  }, [setCompanyState, userCompany, userState]);

  /* Competitions list */
  useEffect(() => {
    authObj.token && authObj.auth && getUserCompetitions();
  }, [authObj.token, authObj.auth, getUserCompetitions]);

  /* User state */
  useEffect(() => {
    const getTryps = async (profileID) => {
      try {
        const topTenTryps = await firebaseState.getTopTryps(profileID, 10);
        setUserTopTryps(topTenTryps);
      } catch (e) {
        setUserTopTryps([]);
      }
    };

    if (authObj.auth) {
      userStateEmail ? updateUserState() : setUserState(currentUser);
      currentUser?.profileID && getTryps(currentUser.profileID);
    }
  }, [
    userStateEmail,
    updateUserState,
    currentUser,
    setUserState,
    authObj.auth,
  ]);

  useEffect(() => {
    const getRank = async () => {
      try {
        let temp = await firebaseState.getGroupRank(
          week.current,
          userState.profileID,
        );
        setRankData(temp);
      } catch (error) {
        setRankData({
          orgRank: null,
          groupRanks: null,
        });
      }
    };
    userState.profileID && getRank();
  }, [userState.profileID]);

  return { currentUser, appFirstLoading };
};

export default useFirebaseAuthentication;
